import React, { useState } from 'react';
import { IRegistroCircularizacao } from '../interfaces/ICircularizacao';
import { gerarItensSolicitados } from '../utils/categoriaConteudo';

interface Props {
  registro: IRegistroCircularizacao;
  empresaAuditada: string;
  dataBase: string;
  emailRetornoEquipe: string;
  nomeSignatario: string;
  onFechar: () => void;
  onImprimir: () => void;
}

const formatarDataPtBr = (iso?: string) => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return dia ? `${dia}/${mes}/${ano}` : iso;
};

// Seção 9/10 — pré-visualização em A4 e impressão/"Salvar como PDF" (via impressão do navegador,
// exclusivamente no Front-End — seção 10.1). "Restaurar Modelo" descarta edições manuais do corpo.
const ModeloCartaPreviewModal: React.FC<Props> = ({
  registro,
  empresaAuditada,
  dataBase,
  emailRetornoEquipe,
  nomeSignatario,
  onFechar,
  onImprimir,
}) => {
  const contatoPrincipal = registro.contatos.find((c) => c.contatoPrincipal) ?? registro.contatos[0];
  // Seção 9 — "Informações específicas": cada categoria tem seu próprio conjunto de itens
  // solicitados (modelo de carta distinto por categoria, não um texto genérico reaproveitado).
  const itensSolicitados = gerarItensSolicitados(registro.categoria);
  const corpoPadrao =
    `Prezados,\n\nNo âmbito dos trabalhos de auditoria independente referentes ao exercício com data-base de ` +
    `${formatarDataPtBr(dataBase)}, solicitamos a gentileza de confirmar, diretamente aos nossos auditores, ` +
    `as informações abaixo relacionadas, mantidas com ${empresaAuditada} na data-base mencionada:` +
    `\n\n${itensSolicitados.map((item) => `- ${item}`).join('\n')}\n\n` +
    `Solicitamos que a resposta seja encaminhada até ${formatarDataPtBr(registro.prazoResposta)}, ` +
    `preferencialmente por meio do e-mail ${emailRetornoEquipe}.`;

  const [editando, setEditando] = useState(false);
  const [corpo, setCorpo] = useState(corpoPadrao);

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
      <div className='circ-modal circ-modal-lg'>
        <div className='circ-modal-header'>
          <h4>Pré-visualização da Carta — {registro.razaoSocialExibicao}</h4>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onFechar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body' style={{ background: '#eef0f4' }}>
          <div className='circ-carta-a4' style={{ width: 'auto', minHeight: 'auto', boxShadow: 'none' }}>
            <div className='circ-carta-cabecalho'>
              <div>
                <div className='circ-carta-titulo'>Carta de Circularização</div>
                <div style={{ fontSize: 11.5, color: '#6b7280' }}>{empresaAuditada}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11.5 }}>
                <div>Data de emissão: {new Date().toLocaleDateString('pt-BR')}</div>
                <div>Data-base: {formatarDataPtBr(dataBase)}</div>
              </div>
            </div>

            <div style={{ marginBottom: 16, fontSize: 12.5 }}>
              <strong>Destinatário:</strong> {registro.razaoSocialExibicao} — {registro.cnpj}
              <br />
              {contatoPrincipal && (
                <>
                  <strong>Aos cuidados de:</strong> {contatoPrincipal.nomeCompleto} ({contatoPrincipal.emailPrincipal})
                </>
              )}
            </div>

            <div className='circ-carta-corpo'>
              {editando ? (
                <textarea
                  className='circ-form-control'
                  rows={10}
                  value={corpo}
                  onChange={(e) => setCorpo(e.target.value)}
                />
              ) : (
                corpo.split('\n\n').map((paragrafo, idx) => {
                  const linhas = paragrafo.split('\n');
                  const ehLista = linhas.length > 1 && linhas.every((l) => l.startsWith('- '));
                  if (ehLista) {
                    return (
                      <ul key={idx} style={{ paddingLeft: 20, marginBottom: 10 }}>
                        {linhas.map((l, i) => (
                          <li key={i}>{l.replace(/^- /, '')}</li>
                        ))}
                      </ul>
                    );
                  }
                  return <p key={idx}>{paragrafo}</p>;
                })
              )}
            </div>

            <div style={{ fontSize: 12.5, marginTop: 16 }}>
              Prazo solicitado para resposta: <strong>{formatarDataPtBr(registro.prazoResposta)}</strong>
              <br />
              E-mail de retorno da equipe de auditoria: <strong>{emailRetornoEquipe}</strong>
            </div>

            <div className='circ-carta-assinatura'>
              <div className='circ-carta-assinatura-linha' />
              {nomeSignatario}
              <br />
              <span style={{ fontSize: 11, color: '#6b7280' }}>Responsável pelos trabalhos de auditoria</span>
            </div>
          </div>
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' onClick={() => { setCorpo(corpoPadrao); setEditando(false); }}>
            Restaurar Modelo
          </button>
          <button type='button' className='btn btn-sm btn-light' onClick={() => setEditando((v) => !v)}>
            {editando ? 'Concluir Edição' : 'Editar'}
          </button>
          <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => window.print()}>
            <i className='bi bi-printer me-1' /> Imprimir Carta
          </button>
          <button type='button' className='btn btn-sm btn-primary' onClick={onImprimir}>
            <i className='bi bi-file-earmark-pdf me-1' /> Salvar como PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModeloCartaPreviewModal;
